import { supabase } from '@/lib/supabase/client'
import type { Database } from '@/lib/supabase/types'

export type ProjectStatus = 'active' | 'paused' | 'closed' | 'archived'
export type ProjectMemberRole = 'admin' | 'data_entry' | 'approver' | 'viewer'

export interface CreateProjectPayload {
  name: string
  code?: string | null
  icon?: string | null
  color?: string | null
  description?: string | null
  department?: string | null
  principal_investigator?: string | null
  institution?: string | null
  irb_reference?: string | null
  start_date?: string | null
  end_date?: string | null
  time_zone?: string
  default_language?: string
  default_calendar?: 'jalali' | 'gregorian'
  data_retention_policy?: string | null
  status?: ProjectStatus
  config?: Record<string, unknown>
}

export interface ExtraMember {
  user_id: string
  role: ProjectMemberRole
}

export interface CreateProjectInput {
  payload: CreateProjectPayload
  initialAdminId: string
  extraMembers: ExtraMember[]
}

export type CreatedProject =
  Database['public']['Functions']['create_project']['Returns']

export async function createProject(
  input: CreateProjectInput,
): Promise<CreatedProject> {
  const { data, error } = await supabase.rpc('create_project', {
    p_payload: input.payload as unknown as Database['public']['Functions']['create_project']['Args']['p_payload'],
    p_initial_admin: input.initialAdminId,
    p_extra_members: input.extraMembers as unknown as Database['public']['Functions']['create_project']['Args']['p_extra_members'],
  })
  if (error) throw error
  return data as CreatedProject
}

export interface UserOption {
  id: string
  full_name: string | null
  email: string | null
}

export async function searchUsers(query: string): Promise<UserOption[]> {
  const trimmed = query.trim()
  let q = supabase
    .from('user_profiles')
    .select('id, full_name, email')
    .order('full_name', { ascending: true })
    .limit(20)

  if (trimmed) {
    q = q.or(`full_name.ilike.%${trimmed}%,email.ilike.%${trimmed}%`)
  }

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as UserOption[]
}
